import React, { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { Heart, Sparkles } from 'lucide-react';

const HeroSection = () => {
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToContact = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      id="home"
      className="relative min-h-screen flex items-center justify-center overflow-hidden text-[#F6F6F6]"
    >
      {/* Background Section */}
      <div
        className="absolute inset-0 z-0"
        style={{
          backgroundImage: 'url("/hero-background.jpg")',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          filter: 'blur(8px) brightness(0.5)',
          transform: 'scale(1.05)'
        }}
      />

      {/* Floating elements with parallax - Blue tones */}
      <div
        className="absolute top-20 left-10 w-20 h-20 rounded-full opacity-20 z-1"
        style={{
          background: 'radial-gradient(circle, #D6E4F0 0%, transparent 70%)',
        }}
      />
      <div
        className="absolute bottom-40 right-20 w-32 h-32 rounded-full opacity-10 z-1"
        style={{
          background: 'radial-gradient(circle, #1E56A0 0%, transparent 70%)',
        }}
      />

      <div className="max-w-7xl mx-auto px-6 pt-40 pb-20 grid md:grid-cols-2 gap-12 items-center relative z-10">
        {/* Left content */}
        <div className="space-y-6">
          <div className="inline-flex items-center space-x-2 px-4 py-2 rounded-full" style={{ background: 'rgba(214, 228, 240, 0.1)', backdropFilter: 'blur(4px)', border: '1px solid rgba(214, 228, 240, 0.2)' }}>
            <Sparkles className="w-4 h-4" style={{ color: '#D6E4F0' }} />
            <span className="text-xs font-medium text-[#D6E4F0]" style={{ textShadow: '0 4px 8px rgba(0,0,0,0.5)' }}>Connecting Generations, Nurturing Minds</span>
          </div>

          <h2
            className="text-4xl md:text-5xl font-bold leading-tight"
            style={{
              fontFamily: 'Merriweather, serif',
              color: '#F6F6F6',
              lineHeight: '1.2',
              textShadow: '0 8px 16px rgba(0,0,0,0.6)'
            }}
          >
            Where Wisdom Meets
            <span
              className="block mt-1"
              style={{
                color: '#0943caff',
                textShadow: '0 1px 4px rgba(35, 35, 35, 0.4)'
              }}
            >
              Wonder
            </span>
          </h2>

          <p className="text-base md:text-lg leading-relaxed text-[#D6E4F0] max-w-lg" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}>
            Sage And Seed connects seniors with neurodegenerative conditions with young adults for one-to-one online video conversations. Gentle social engagement that improves mood, reduces depressive symptoms, and enhances quality of life.
          </p>

          <div className="flex flex-wrap gap-4">
            <Button
              onClick={scrollToContact}
              size="lg"
              className="rounded-full px-8 py-6 text-base font-semibold transition-all duration-300"
              style={{
                background: 'linear-gradient(135deg, #1E56A0 0%, #163172 100%)',
                color: '#F6F6F6',
                border: 'none'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-3px)';
                e.currentTarget.style.boxShadow = '0 12px 32px rgba(30, 86, 160, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <Heart className="w-5 h-5 mr-2" />
              Join as a Volunteer
            </Button>

            <Button
              onClick={scrollToContact}
              size="lg"
              variant="outline"
              className="rounded-full px-8 py-6 text-base font-semibold transition-all duration-300"
              style={{
                borderColor: '#D6E4F0',
                color: '#F6F6F6',
                borderWidth: '2px',
                background: 'rgba(214, 228, 240, 0.1)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-3px)';
                e.currentTarget.style.background = 'rgba(214, 228, 240, 0.2)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.background = 'rgba(214, 228, 240, 0.1)';
              }}
            >
              For Caregivers
            </Button>
          </div>

          {/* Trust indicators */}
          <div className="flex items-center space-x-6 pt-4">
            <div className="text-sm">
              <div className="font-semibold text-2xl text-[#F6F6F6]">1-to-1</div>
              <div className="text-[#D6E4F0]">Video Sessions</div>
            </div>
            <div className="w-px h-12 bg-[#D6E4F0]/30"></div>
            <div className="text-sm">
              <div className="font-semibold text-2xl text-[#F6F6F6]">100%</div>
              <div className="text-[#D6E4F0]">Privacy Focused</div>
            </div>
          </div>
        </div>

        {/* Right visual content is kept but framed differently if needed,
            or we can hide it if the hero background image is enough.
            The user asked for the image as background, so maybe we should keep the right side content subtle or remove the duplicate image there. */}
        <div
          className="relative hidden md:block max-w-[400px] ml-auto"
        >
          {/* Main Visual Frame */}
          <div className="relative rounded-[2rem] overflow-hidden shadow-2xl border-4 border-white/10" style={{ aspectRatio: '4/5' }}>
            <img
              src="/hero-right.png"
              alt="Elderly and young person connecting"
              className="w-full h-full object-cover"
            />
            <div
              className="absolute inset-0"
              style={{
                background: 'linear-gradient(180deg, transparent 60%, rgba(0,0,0,0.4) 100%)'
              }}
            />
          </div>

          {/* Floating card */}
          <div
            className="absolute -bottom-6 -left-6 p-6 rounded-2xl shadow-xl"
            style={{
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(12px)',
              maxWidth: '280px',
              zIndex: 20
            }}
          >
            <div className="flex items-start space-x-3">
              <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: '#737fe9ff' }}>
                <Heart className="w-6 h-6" style={{ color: '#F6F6F6' }} />
              </div>
              <div>
                <div className="font-semibold text-slate-800">Meaningful Moments</div>
                <div className="text-sm mt-1 text-slate-600">
                  Every conversation creates lasting joy and connection
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
